import {
  getDoorOpeningHeight,
  isGarageDoorOperationType,
  isPocketDoorOperationType,
} from "building-generation/elements/doors/doorUtils";
import { levelName } from "building-generation/elements/level/levelTools";
import type { Building } from "building-types/building/buildingTypes";
import { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import type { DoorKeyAttributes, DoorKeyMeasures } from "building-types/dataSequences/builtin/DoorKAM";
import type { DoorKAM, DoorKAMOptions } from "building-types/dataSequences/builtin/DoorKAM";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { DoorUnit, GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import { SwingSide } from "building-types/doors/doorTypes";
import { buildingEntityPickHandle } from "building-types/entities/buildingEntityPickHandle";
import type { PerimeterStreetOrientationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass, RoomType } from "building-types/rooms/roomTypesEnum";
import type { Table } from "common/data/table";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import {
  keyAttributeNew,
  keyMeasureNew,
  keyAttributeOptionsNew,
  keyMeasureOptionsNew,
} from "doc/utils/dataSequenceTools";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import { coveredPerimeterFacesByLevelLookup } from "lib/house/coveredPerimeterFacesByLevelLookup";
import type { ConfigurableTable } from "lib/house/dataSequences/dataSequenceConfigurableTable";
import {
  categoryColumnNew,
  configurableTableNew,
  numberColumnNew,
  SortBy,
  stringColumnNew,
  stringConverterColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";
import { delimitedList } from "lib/house/dataSequences/dataSequenceFormatting";

export function makeDoorsTable(
  doors: Table<GeneratedDoorPlacement>,
  streetOrientationByLevel: Table<PerimeterStreetOrientationTable>,
): ConfigurableTable {
  return configurableTableNew<GeneratedDoorPlacement, GeneratedDoorPlacement>({
    iterables: doors,
    requirement: doorFilter,
    columns: [
      categoryColumnNew("Doors"),
      stringColumnNew({
        name: "Interior/Exterior",
        fieldFunc: doorAccess,
        sortBy: SortBy.Descending,
      }),
      stringColumnNew({
        name: "Tag",
        fieldFunc: (door) => door.descriptor.reportedName,
      }),
      stringColumnNew({
        name: "Panels",
        fieldFunc: (door) => doorType(door),
        sortBy: SortBy.Ascending,
      }),
      stringConverterColumnNew({
        name: "Swing / Handing",
        fieldFunc: (door) => doorSwingAndHanding(door),
        stringFormatter: delimitedList(),
      }),
      numberColumnNew({
        name: "Width (IN)",
        fieldFunc: (door) => door.descriptor.width,
        sortBy: SortBy.Ascending,
        hidden: true,
        useInDiff: false,
      }),
      numberColumnNew({
        name: "Height (IN)",
        fieldFunc: (door) => door.descriptor.height,
        sortBy: SortBy.Ascending,
        hidden: true,
        useInDiff: false,
      }),
      stringConverterColumnNew({
        name: "Details",
        fieldFunc: doorDetails(streetOrientationByLevel),
        stringFormatter: delimitedList(),
      }),
    ],
  });
}

function doorFilter(door: GeneratedDoorPlacement): GeneratedDoorPlacement | null {
  const { width, height } = door.descriptor;
  const { opening } = door;
  if (!opening || width === undefined || height === undefined) {
    return null;
  }
  if (
    door.descriptor.operationType === DoorOperationType.FIREPLACE ||
    isGarageDoorOperationType(door) ||
    door.descriptor.operationType === DoorOperationType.OPEN ||
    door.descriptor.operationType === DoorOperationType.ARC ||
    door.descriptor.operationType === DoorOperationType.PORTICO
  ) {
    return null;
  }
  return door;
}

// Mimicking legacy schedule filtering for backwards compatibility
function doorScheduleFilter(
  doorUnit: DoorUnit,
  includeGarageDoors: boolean,
  filter?: (entity: GeneratedDoorPlacement) => boolean,
): DoorUnit | null {
  const { doorPlacement } = doorUnit;
  const { width, height } = doorPlacement.descriptor;
  if (!width || !height) {
    return null;
  }
  if (
    doorPlacement.descriptor.operationType === DoorOperationType.OPEN ||
    doorPlacement.descriptor.operationType === DoorOperationType.FIREPLACE
  ) {
    return null;
  }
  if (doorPlacement.descriptor.operationType === DoorOperationType.ARC && !doorPlacement.descriptor.requiresDoorSwing) {
    return null;
  }
  if (!includeGarageDoors && isGarageDoorOperationType(doorPlacement)) {
    return null;
  }
  if (filter && !filter(doorPlacement)) {
    return null;
  }
  return doorUnit;
}

export type Access = "interior" | "exterior" | "garage";

function doorAccess(door: GeneratedDoorPlacement): Access {
  const { frontRoom, backRoom } = door;
  if (isGarageDoorOperationType(door)) {
    return "garage";
  }
  if (!frontRoom || !isInteriorRoomEnclosure[frontRoom.attributes.roomEnclosure]) {
    return "exterior";
  }
  if (!backRoom || !isInteriorRoomEnclosure[backRoom.attributes.roomEnclosure]) {
    return "exterior";
  }
  return "interior";
}

function doorType(door: GeneratedDoorPlacement) {
  let type = door.descriptor.doorLeafCount === 2 ? "double" : "single";

  if (isGarageDoorOperationType(door)) {
    return "NA";
  }

  if (door.descriptor.operationType === DoorOperationType.BYPASS) {
    switch (door.descriptor.doorLeafCount) {
      case 4: {
        type = "4 leaves";
        break;
      }
      case 3: {
        type = "3 leaves";
        break;
      }
      default: {
        break;
      }
    }
  }
  return type;
}

type DoorDetail =
  | "garage service door"
  | "front door"
  | "side door"
  | "rear door"
  | "LH"
  | "RH"
  | "LHR"
  | "RHR"
  | "Sliding"
  | "Pocket"
  | "Closet"
  | "NA"
  | "Privacy"
  | "Keyed"
  | "Attic";

function doorDetails(
  streetOrientationByLevel: Table<PerimeterStreetOrientationTable>,
): (door: GeneratedDoorPlacement) => string[] {
  return (door: GeneratedDoorPlacement): string[] => {
    const { frontRoom, backRoom } = door;

    const details: DoorDetail[] = [];

    if (door.descriptor.operationType === DoorOperationType.ATTIC) {
      details.push("Attic");
      return details;
    }

    //TODO: garage service door will need to use connected open room groups, since minor hallways inside
    // of the garage can obfuscate the room door relationship
    if (frontRoom && frontRoom.attributes.roomClass === RoomClass.GARAGE) {
      if (backRoom && isInteriorRoomEnclosure[backRoom.attributes.roomEnclosure]) {
        details.push("garage service door");
        details.push("Keyed");
      }
    } else if (backRoom && backRoom.attributes.roomClass === RoomClass.GARAGE) {
      if (frontRoom && isInteriorRoomEnclosure[frontRoom.attributes.roomEnclosure]) {
        details.push("garage service door");
        details.push("Keyed");
      }
    }

    if (door.descriptor.operationType === DoorOperationType.BYPASS) {
      details.push("Sliding");
    }

    if (
      door.backRoom?.type === RoomType.BEDROOM ||
      door.frontRoom?.type === RoomType.BEDROOM ||
      door.backRoom?.type === RoomType.BATHFULL ||
      door.frontRoom?.type === RoomType.BATHFULL
    ) {
      details.push("Privacy");
    }

    if (door.descriptor.operationType === DoorOperationType.CLOSET) {
      details.push("Closet");
    }

    const levelId = doorLevel(frontRoom, backRoom);
    const streetOrientationTable = levelId !== null ? streetOrientationByLevel[levelId] : {};

    const he = door.halfEdgeIndex;
    const adjHe = planarGraphAdjacentHalfEdge(he);

    let streetOrientation = streetOrientationTable[he];
    if (!streetOrientation) {
      streetOrientation = streetOrientationTable[adjHe];
    }

    if (streetOrientation) {
      switch (streetOrientation) {
        case StreetOrientation.FRONT: {
          details.push("front door");
          details.push("Keyed");
          break;
        }
        case StreetOrientation.LEFT:
        case StreetOrientation.RIGHT: {
          details.push("side door");
          details.push("Keyed");
          break;
        }
        case StreetOrientation.BACK: {
          details.push("rear door");
          details.push("Keyed");
          break;
        }
      }
    }

    return details;
  };
}

function doorSwingAndHanding(door: GeneratedDoorPlacement): string[] {
  const details: DoorDetail[] = [];

  if (isPocketDoorOperationType(door)) {
    details.push("Pocket");
    return details;
  }

  if (door.descriptor.doorLeafCount > 1) {
    details.push("NA");
    return details;
  }

  if (door.swingSide === SwingSide.RIGHT) {
    details.push("LH");
    if (door.reverse) {
      details.push("LHR");
    }
  } else if (door.swingSide === SwingSide.LEFT) {
    details.push("RH");
    if (door.reverse) {
      details.push("RHR");
    }
  } else {
    details.push("NA");
  }

  return details;
}

function doorLevel(frontRoom: Room, backRoom: Room): string | null {
  if (frontRoom && frontRoom.level.id !== undefined) {
    return frontRoom.level.id;
  } else if (backRoom && backRoom.level.id !== undefined) {
    return backRoom.level.id;
  }

  // assume door without room on ground
  console.warn("door level assumed to be 0");
  return null;
}

function doorSwing(door: GeneratedDoorPlacement): string {
  if (door.descriptor.doorLeafCount > 1) {
    return "NA";
  }

  if (isGarageDoorOperationType(door)) {
    return "NA";
  }

  if (door.swingSide) {
    return door.swingSide;
  }

  return "NA";
}

function doorEntityName(door: GeneratedDoorPlacement): string {
  const {
    descriptor: { id: propType, width, height, doorLeafCount },
  } = door;

  const details: string[] = [];
  const access = doorAccess(door).slice(0, 2);
  details.push(access);

  const dims: string = `${width}/${height}`;
  details.push(dims);

  const swing = doorSwing(door);
  details.push(swing);

  const leafCount = `L${doorLeafCount}`;
  details.push(leafCount);

  details.push(propType);

  return delimitedList("-")(details);
}

enum DoorHanding {
  NONE = "NONE",
  LEFT = "LEFT",
  RIGHT = "RIGHT",
  DH = "DH",
}

function doorHanding(door: GeneratedDoorPlacement): DoorHanding {
  if (isGarageDoorOperationType(door) || isPocketDoorOperationType(door)) {
    return DoorHanding.NONE;
  }
  if (!door.descriptor.propIdPrefix?.includes("Double") || door.descriptor.operationType !== DoorOperationType.DOUBLE) {
    if (door.swingSide === SwingSide.LEFT) {
      return DoorHanding.RIGHT;
    } else if (door.swingSide === SwingSide.RIGHT) {
      return DoorHanding.LEFT;
    } else {
      return DoorHanding.NONE;
    }
  } else {
    return DoorHanding.DH;
  }
}

function makeDoorKAM(
  doorUnit: DoorUnit,
  streetOrientationByLevel: Table<PerimeterStreetOrientationTable>,
  levelNameLookup: (levelId: string) => string,
): DoorKAM {
  const { doorPlacement } = doorUnit;
  const descriptor = doorPlacement.descriptor;

  const width = descriptor.width ?? 0;
  const height = getDoorOpeningHeight(descriptor);
  const level = doorLevel(doorPlacement.frontRoom, doorPlacement.backRoom);
  return {
    type: "DOOR",
    attributes: {
      class: keyAttributeNew(doorAccess(doorPlacement)),
      tag: keyAttributeNew(doorPlacement.reportedName ?? descriptor.reportedName),
      panels: keyAttributeNew(doorType(doorPlacement)),
      swing: keyAttributeNew(doorSwing(doorPlacement)),
      handing: keyAttributeNew(doorHanding(doorPlacement)),
      details: keyAttributeNew(delimitedList()(doorDetails(streetOrientationByLevel)(doorPlacement))),
      floor: keyAttributeNew(level ? levelNameLookup(level) : ""),
      width: keyAttributeNew(width.toString(), "INCHES"),
      height: keyAttributeNew(height.toString(), "INCHES"),
      area: keyAttributeNew((width * height).toString(), "SQ IN"),
      entityName: keyAttributeNew(doorEntityName(doorPlacement)),
      leafCount: keyAttributeNew(descriptor.doorLeafCount.toString()),
      propType: keyAttributeNew(descriptor.id),
    },
    measures: {
      width: keyMeasureNew(width, "INCHES"),
      height: keyMeasureNew(height, "INCHES"),
      area: keyMeasureNew(width * height, "SQ IN"),
      count: keyMeasureNew(1),
    },
    handles: [buildingEntityPickHandle(doorUnit)],
  };
}

export const EMPTY_DOOR_KAM: DoorKAM = {
  type: "DOOR",
  attributes: {
    class: keyAttributeNew(""),
    tag: keyAttributeNew(""),
    panels: keyAttributeNew(""),
    swing: keyAttributeNew(""),
    handing: keyAttributeNew(""),
    details: keyAttributeNew(""),
    floor: keyAttributeNew(""),
    width: keyAttributeNew("", "INCHES"),
    height: keyAttributeNew("", "INCHES"),
    area: keyAttributeNew("", "SQ IN"),
    entityName: keyAttributeNew(""),
    leafCount: keyAttributeNew(""),
    propType: keyAttributeNew(""),
  },
  measures: {
    width: keyMeasureNew(0, "INCHES"),
    height: keyMeasureNew(0, "INCHES"),
    area: keyMeasureNew(0, "SQ IN"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultDoorKAMOptions(): DoorKAMOptions {
  return {
    attributes: {
      width: keyAttributeOptionsNew({ unit: "INCHES" }),
      height: keyAttributeOptionsNew({ unit: "INCHES" }),
      area: keyAttributeOptionsNew({ definesEntity: false, unit: "SQ IN" }),
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
      leafCount: keyAttributeOptionsNew({ definesEntity: false }),
      propType: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      width: keyMeasureOptionsNew({ unit: "INCHES" }),
      height: keyMeasureOptionsNew({ unit: "INCHES" }),
      area: keyMeasureOptionsNew({ unit: "SQ IN" }),
    },
  };
}

export const defaultDoorKAMSort = (a: DoorKAM, b: DoorKAM) => {
  return (
    b.attributes.class.value.localeCompare(a.attributes.class.value) ||
    a.attributes.panels.value.localeCompare(b.attributes.panels.value) ||
    a.attributes.width.value.localeCompare(b.attributes.width.value) ||
    a.attributes.height.value.localeCompare(b.attributes.height.value)
  );
};

/** @deprecated - use the Building Query API instead */
export function buildingDoorKAMs(
  { doorUnits, planarGraphPerimeters, levels }: Building,
  filter?: (entity: GeneratedDoorPlacement) => boolean,
  withGarageDoors: boolean = false,
): DoorKAM[] {
  const streetOrientationLookup = coveredPerimeterFacesByLevelLookup(planarGraphPerimeters);
  const levelNameLookup = (levelId: string) => levelName(levels[levelId]) ?? "";
  const result: DoorKAM[] = [];
  for (const doorId in doorUnits) {
    const doorUnit = doorUnits[doorId];
    if (doorScheduleFilter(doorUnit, withGarageDoors, filter)) {
      result.push(makeDoorKAM(doorUnit, streetOrientationLookup, levelNameLookup));
    }
  }
  return result;
}

export const DOOR_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<DoorKeyAttributes, DoorKeyMeasures> = {
  attributes: {
    entityName: {
      hidden: true,
    },
    leafCount: {
      hidden: true,
    },
    propType: {
      hidden: true,
    },
    area: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_DOOR: (keyof DoorKeyAttributes)[] = ["height", "width", "swing", "class", "leafCount", "propType"];
